// components/UserTrades.jsx
import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Skeleton,
    Avatar,
    Pagination,
    Box,
    useTheme
} from '@mui/material';
import { formatTokenAmount, transformToBTC } from '../../utils/helpers';
import { useUserTrades } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';

const UserTrades = ({ userId }) => {
    const { data: trades, loading, error, page, setPage, hasNextPage } =
        useUserTrades({ userId, limit: 10 });
    const navigate = useNavigate();
    const theme = useTheme();
    const { price: btcPrice } = useBitcoinPrice();

    if (loading) {
        return (
            <Skeleton
                variant="rectangular"
                sx={{
                    borderRadius: 2,
                    width: '100%',
                    height: 300,
                }}
            />
        );
    }
    if (error) {
        return (
            <Typography color="error" sx={{ m: 2 }}>
                Error: {error.message}
            </Typography>
        );
    }

    return (
        <Card
            sx={{
                borderRadius: 2,
                boxShadow: 3,
                m: 2,
            }}
        >
            <CardContent>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 'bold', mb: 2 }}
                >
                    Trades
                </Typography>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Token</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Token Amt</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>BTC Amt</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {trades.map((trade, idx) => (
                            <TableRow
                                key={idx}
                                onClick={() => window.open(`/tokens/${ trade.tokenid }`)}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'light'
                                            ? 'rgba(0,0,0,0.04)'
                                            : 'rgba(255,255,255,0.1)'
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Avatar
                                        src={`https://images.odin.fun/token/${ trade.tokenid }`}
                                        alt={trade.token.ticker}
                                        sx={{ width: 24, height: 24 }}
                                    />
                                    {trade.token.ticker}
                                </TableCell>
                                <TableCell>{trade.type}</TableCell>
                                <TableCell>
                                    {formatTokenAmount(trade.amount_token)}
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2">
                                            {transformToBTC(trade.amount_btc)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ${((trade.amount_btc / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>{trade.price}</TableCell>
                                <TableCell>
                                    {new Date(trade.time).toLocaleString()}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={hasNextPage ? page + 1 : page}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                        size="small"
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export default UserTrades;
